import { useEffect, useMemo, useRef, useState } from 'react'
import { EventRuntime } from '../lib/EventRuntime'
import { loadCharacters, getCurrentCharacterId, setCurrentCharacterId } from '../lib/characterState'
import { PRESET_ANIMATIONS, togglePreset, isPresetPlaying, isPresetAvailable } from '../lib/presetAnimations.js'
import { isFaceAnimEnabled, setFaceAnimEnabled, subscribeFaceAnim } from '../lib/faceAnimState.js'
import { CustomAssetManager } from '../lib/CustomAssetManager'

/**
 * Floating panel for manually firing animation events — three sections:
 *
 *   • Gesture   — fires `onGesture` with { animName }. The blueprint's
 *                 ev_on_gesture chain calls playGesture, which drives the
 *                 face override via FaceClipPlayer.manualPlay AND the body
 *                 layer via GestureChannel.playBodyGesture.
 *   • Prefab    — fires `onPrefabTrigger` with { prefab, animName }. The
 *                 blueprint's ev_prefab_trigger chain sets ConversationalState
 *                 = 'prefab' (body + face SMs crossfade), plays greeting.wav,
 *                 waits ~9 s, returns to idle.
 *   • preset_animations — plays a whole-body clip DIRECTLY, bypassing the
 *                 graph entirely (OGS mesh preview, else the __DIRECT_PLAY__
 *                 override). Toggle-style: the same button stops it.
 *
 * Hidden by default. Enable with `?anim=1` in the URL or set
 * `localStorage.anim = '1'`.
 *
 * Also exposes console helpers regardless of panel visibility:
 *   window.__TEST_GESTURE__(name)
 *   window.__TEST_PREFAB__(name)
 *   window.__TEST_PRESET__(name)
 *   window.__TEST_SAY__(text)   — make the character say an exact line (TextOut)
 */

const QUICK_GESTURES = ['sigh', 'laugh', 'lean in', 'shrug', 'nod', 'roll eyes', 'smile']
const QUICK_TRIGGERS = [
  'favPhysics', 'howAreYou', 'welcomeToChaos', 'energyDrinks', 'findSignatures',
  'howStartup', 'whatInvestorsSay', 'workinOn', 'CosmicMicrowaveBackground',
  'ToughPitch', 'confirmationBias', 'cosmicRadiationPatterns',
]

// Prefab names the server can send as `NewResponse.trigger`. Match what's in
// aosTools/threejs/build_anims.py's CONVERSATIONAL_ANIM_FBXES (the `prefab_*` entries).
// Note: conversational.json's prefab SM state currently hard-codes
// `prefab_greeting` as the clip + greeting.wav as the audio, so only greeting
// plays end-to-end today. Other names fire the event chain but reuse that
// content — extend the blueprint to diversify.
const PREFAB_NAMES = [
  'greeting',
  'guest_intro',
  'dads_motto',
  'lost_my_mind',
  'funding',
  'good_luck',
]

function fireGesture(name) {
  if (!name) return
  EventRuntime.fireEvent('onGesture', { animName: name, actionText: '', sentenceNum: 0 })
}

function firePrefab(name) {
  if (!name) return
  EventRuntime.fireEvent('onPrefabTrigger', { prefab: name, animName: name })
}

// Preset animations bypass EventRuntime entirely — they drive the mesh directly.
function firePreset(name) {
  return togglePreset(name)
}

function sendDebugCommand(command) {
  const client = typeof window !== 'undefined' ? window.__CONVORCHER_CLIENT__ : null
  if (!client) { console.warn('[AnimTest] __CONVORCHER_CLIENT__ not available'); return false }
  return client.sendDebugCommand(command)
}

function sendDebugTrigger(name) {
  const client = typeof window !== 'undefined' ? window.__CONVORCHER_CLIENT__ : null
  if (!client) { console.warn('[AnimTest] __CONVORCHER_CLIENT__ not available'); return false }
  return client.sendDebugCommandTrigger(name)
}

// Make the character say an exact line — the Say Line node's send, without a
// graph edit. TextOut is the character's own turn: the server extracts
// bracketed actions as gestures and uses the quality TTS model, so
// "[nodding] nice work! [clapping]" speaks AND gestures.
function sayLine(text) {
  const client = typeof window !== 'undefined' ? window.__CONVORCHER_CLIENT__ : null
  if (!client) { console.warn('[AnimTest] __CONVORCHER_CLIENT__ not available'); return false }
  return client.sendTextOut(text)
}

const SMALL_BTN = {
  background: 'var(--m-panel-hi)', color: 'var(--m-fg-2)', border: '1px solid var(--m-line-hi)',
  fontFamily: 'var(--m-font)', fontSize: 10, padding: '2px 6px', cursor: 'pointer',
}

const PLAY_BTN = {
  background: 'color-mix(in srgb, var(--m-online) 18%, var(--m-panel))', color: 'var(--m-online)', border: '1px solid var(--m-online)',
  fontFamily: 'var(--m-font)', fontSize: 11, padding: '2px 10px', cursor: 'pointer',
}

const STOP_BTN = {
  ...PLAY_BTN,
  background: 'color-mix(in srgb, var(--m-warn) 18%, var(--m-panel))', color: 'var(--m-warn)', border: '1px solid var(--m-warn)',
}

// A preset whose clip isn't in this character's bundle: visible but inert, so a
// missing clip reads as "not loaded here" rather than a button that does nothing.
const DISABLED_BTN = {
  ...PLAY_BTN,
  background: 'var(--m-panel-hi)', color: 'var(--m-fg-3)', border: '1px solid var(--m-line)',
  cursor: 'not-allowed',
}

const SELECT_STYLE = {
  flex: 1, background: 'var(--m-bg-graph)', color: 'var(--m-fg)', border: '1px solid var(--m-line-hi)',
  fontFamily: 'var(--m-font)', fontSize: 11, padding: '2px 4px',
}

// Colour per active-clip source — used by the Active list chips.
const SOURCE_CHIP_COLORS = {
  body:     { bg: '#1c2838', fg: '#7aa8d0' },
  face:     { bg: '#382418', fg: '#d0a070' },
  gesture:  { bg: '#2c1c38', fg: '#b080d0' },
  livelink: { bg: '#1c3828', fg: '#70c090' },
}

/**
 * Read current active clips from the runtime globals. No allocations on the
 * render path — called from a 100ms poll gated on debugState.tick, so cost
 * lands only when weights actually change.
 */
function collectActive() {
  const out = []
  const actions = typeof window !== 'undefined' ? window.__AVATAR_ACTIONS__ : null
  const faceP   = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
  const chan    = typeof window !== 'undefined' ? window.__GESTURE_CHANNEL__ : null
  const dbg     = typeof window !== 'undefined' ? window.__DEBUG_STATE__ : null

  // Body: the graph's own players (one per Play Clip node), then the clip
  // templates — which only direct play drives now.
  const players = typeof window !== 'undefined' ? window.__ANIM_PLAYERS__ : null
  for (const p of players?.live?.() ?? []) {
    if (p.weight > 0) out.push({ source: 'body', name: p.clipName, weight: p.weight })
  }
  if (actions) {
    for (const [name, action] of Object.entries(actions)) {
      const w = action.getEffectiveWeight?.() ?? 0
      if (w > 0) out.push({ source: 'body', name, weight: w })
    }
  }

  if (faceP?._active) {
    const faceWeights = dbg?.faceWeights ?? {}
    for (const name of Object.keys(faceP._active)) {
      out.push({ source: 'face', name, weight: faceWeights[name] ?? 0 })
    }
  }

  // Each gesture on screen — the newest and any still fading out — at its
  // current weight.
  for (const g of chan?.players?.() ?? []) {
    out.push({ source: 'gesture', name: g.clipName, weight: g.weight })
  }

  const ll = dbg?.clipWeights?.['__livelink__'] ?? 0
  if (ll > 0) out.push({ source: 'livelink', name: '__livelink__', weight: ll })

  // Fixed sort: source priority, then weight desc within a source.
  const SRC_ORDER = { body: 0, face: 1, gesture: 2, livelink: 3 }
  out.sort((a, b) => {
    const s = (SRC_ORDER[a.source] ?? 9) - (SRC_ORDER[b.source] ?? 9)
    return s !== 0 ? s : b.weight - a.weight
  })
  return out
}

function MiniBar({ value }) {
  const w = Math.max(0, Math.min(1, value))
  return (
    <div style={{ width: 40, height: 5, background: 'var(--m-bg-graph)', border: '1px solid var(--m-line)', borderRadius: 1 }}>
      <div style={{ width: `${w * 100}%`, height: '100%', background: 'var(--m-online)' }} />
    </div>
  )
}

export default function AnimTestPanel({ visible: visibleProp }) {
  const visibleFromEnv = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('anim') === '1'
      || localStorage.getItem('anim') === '1'
  }, [])
  const visible = visibleProp ?? visibleFromEnv

  const [faceAnim, setFaceAnim] = useState(isFaceAnimEnabled())
  useEffect(() => subscribeFaceAnim(setFaceAnim), [])
  const [gestures, setGestures] = useState([])
  const [gesture,  setGesture]  = useState('sigh')
  const [prefab,   setPrefab]   = useState('greeting')
  const [lastFired, setLastFired] = useState('')
  const [sayText, setSayText] = useState('')
  const [debugCmd,  setDebugCmd]  = useState('')
  const [characters, setCharacters] = useState([])
  const [charId, setCharId] = useState('')

  // Live list of every clip currently playing — tick-throttled so we only
  // re-render when weights actually change.
  const [active, setActive] = useState([])
  const lastTickRef = useRef(-1)
  useEffect(() => {
    if (!visible) return
    const id = setInterval(() => {
      const tick = window.__DEBUG_STATE__?.tick ?? 0
      if (tick === lastTickRef.current) return
      lastTickRef.current = tick
      setActive(collectActive())
    }, 100)
    return () => clearInterval(id)
  }, [visible])

  // Preset playing/available state lives on window (the OGS preview and the
  // __DIRECT_PLAY__ override), so poll it. Returning `prev` unchanged keeps this
  // from re-rendering the panel every 200ms.
  const [presets, setPresets] = useState({})
  useEffect(() => {
    if (!visible) return
    const read = () => setPresets(prev => {
      const next = {}
      let changed = false
      for (const name of PRESET_ANIMATIONS) {
        next[name] = { playing: isPresetPlaying(name), available: isPresetAvailable(name) }
        const p = prev[name]
        if (!p || p.playing !== next[name].playing || p.available !== next[name].available) changed = true
      }
      return changed ? next : prev
    })
    read()
    const id = setInterval(read, 200)
    return () => clearInterval(id)
  }, [visible])

  // Hair shading controls with polling at every 500 second
  const [hairFlags, setHairFlags] = useState(null)
  useEffect(() => {
    if (!visible) return
    let mounted = true
    const refresh = () => {
      const h = typeof window !== 'undefined' ? window.hair : null
      if (!mounted) return
      setHairFlags(h?.flags ? { ...h.flags } : null)
    }
    refresh()
    const id = setInterval(refresh, 500)
    return () => { mounted = false; clearInterval(id) }
  }, [visible])
  const setHairFlag = (name, value) => {
    const h = typeof window !== 'undefined' ? window.hair : null
    if (!h?.setFlag) return
    try { h.setFlag(name, value) } catch (e) { console.warn('[AnimTest] hair.setFlag threw:', e) }
    setHairFlags((prev) => (prev ? { ...prev, [name]: value } : prev))
  }

  // Populate the gesture dropdown from FaceClipPlayer's loaded gesture map so
  // it can't drift out of sync with the character's gestures table.
  //
  // Re-read on every CustomAssetManager change, not once: the list used to be
  // read a single time and never again, so a gesture created in the Composer
  // never appeared here (and an edited one silently kept its old clips) until
  // a page reload. A gesture write now refreshes the runtime map BEFORE it
  // notifies, so this read always sees the mapping the save produced.
  useEffect(() => {
    if (!visible) return
    let cancelled = false
    const read = () => {
      if (cancelled) return false
      const p = window.__FACE_CLIP_PLAYER__
      if (!p?._loaded || !p._gestures) return false
      const names = Object.keys(p._gestures).filter(k => k !== '*' && !k.startsWith('_'))
      names.sort()
      setGestures(names)
      return true
    }
    // Poll only until the avatar's first load populates the map.
    const tick = () => { if (!read() && !cancelled) setTimeout(tick, 200) }
    tick()
    const unsubscribe = CustomAssetManager.subscribe(read)
    return () => { cancelled = true; unsubscribe?.() }
  }, [visible])

  // Populate the character list once the panel is visible. Character
  // switching reloads the page — the rig path + material are wired into
  // AvatarModel's one-shot load effect, no hot-swap.
  useEffect(() => {
    if (!visible) return
    let cancelled = false
    loadCharacters()
      .then(list => {
        if (cancelled) return
        setCharacters(list)
        setCharId(getCurrentCharacterId(list))
      })
      .catch(err => console.warn('[AnimTest] character list failed:', err))
    return () => { cancelled = true }
  }, [visible])

  // Expose console helpers regardless of panel visibility.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__TEST_GESTURE__ = fireGesture
    window.__TEST_PREFAB__  = firePrefab
    window.__TEST_PRESET__  = firePreset
    window.__TEST_SAY__     = sayLine
    return () => {
      if (window.__TEST_GESTURE__ === fireGesture) delete window.__TEST_GESTURE__
      if (window.__TEST_PREFAB__  === firePrefab)  delete window.__TEST_PREFAB__
      if (window.__TEST_PRESET__  === firePreset)  delete window.__TEST_PRESET__
      if (window.__TEST_SAY__     === sayLine)    delete window.__TEST_SAY__
    }
  }, [])

  if (!visible) return null

  const doGesture = (name) => { fireGesture(name); setLastFired(`gesture: ${name}`) }
  const doPrefab  = (name) => { firePrefab(name);  setLastFired(`prefab: ${name}`) }
  const doPreset = (name) => {
    const state = firePreset(name)
    setLastFired(state === 'missing' ? `preset (not loaded): ${name}` : `preset: ${name} ${state.endsWith('stop') ? '■' : '▶'}`)
    setPresets(prev => ({ ...prev, [name]: { ...prev[name], playing: isPresetPlaying(name) } }))
  }
  const doSay = () => {
    const line = sayText.trim()
    if (!line) return
    const ok = sayLine(line)
    setLastFired(ok ? `say: ${line}` : `say (not connected): ${line}`)
  }
  const doDebugCmd = (cmd) => {
    const ok = sendDebugCommand(cmd)
    setLastFired(ok ? `DEBUG: ${cmd}` : `DEBUG (not connected): ${cmd}`)
  }
  const doDebugTrigger = (name) => {
    const ok = sendDebugTrigger(name)
    setLastFired(ok ? `trigger: ${name}` : `trigger (not connected): ${name}`)
  }

  return (
    <div style={{
      position: 'absolute',
      top: 10, right: 10,
      background: 'color-mix(in srgb, var(--m-panel) 88%, transparent)', border: '1px solid var(--m-line-hi)', borderRadius: 6,
      padding: '8px 10px', fontFamily: 'var(--m-font)', fontSize: 11,
      color: 'var(--m-fg)', zIndex: 20, width: 240,
    }}>
      <div style={{ color: 'var(--m-accent)', marginBottom: 8, letterSpacing: 0.5 }}>ANIM TEST</div>

      {/* ── Face anim ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>FACE ANIM</span>
        <button
          onClick={() => setFaceAnimEnabled(!faceAnim)}
          title="OFF holds the face at neutral — speech, clips, blink and sliders all stand down"
          style={{ ...SMALL_BTN, color: faceAnim ? 'var(--m-accent)' : 'var(--m-fg-3)' }}
        >{faceAnim ? 'ON' : 'OFF'}</button>
      </div>

      {/* ── Character picker ─────────────────────────────────────────── */}
      {characters.length > 1 && (
        <>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>CHARACTER (reloads)</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            <select
              value={charId}
              onChange={e => {
                const id = e.target.value
                setCharId(id)
                setCurrentCharacterId(id)
                window.location.reload()
              }}
              style={SELECT_STYLE}
            >
              {characters.map(c => (
                <option key={c.id} value={c.id}>{c.label ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* ── Gestures ─────────────────────────────────────────────────── */}
      <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>GESTURE (onGesture)</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <select value={gesture} onChange={e => setGesture(e.target.value)} style={SELECT_STYLE}>
          {gestures.length === 0 && <option>(loading…)</option>}
          {gestures.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={() => doGesture(gesture)} style={PLAY_BTN}>▶ play</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 10 }}>
        {QUICK_GESTURES.map(n => (
          <button key={n} onClick={() => doGesture(n)} title={`fire onGesture "${n}"`} style={SMALL_BTN}>{n}</button>
        ))}
      </div>

      {/* ── Prefabs ──────────────────────────────────────────────────── */}
      <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>PREFAB (onPrefabTrigger)</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <select value={prefab} onChange={e => setPrefab(e.target.value)} style={SELECT_STYLE}>
          {PREFAB_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={() => doPrefab(prefab)} style={PLAY_BTN}>▶ play</button>
      </div>
      <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginTop: 2 }}>
        only <span style={{ color: 'var(--m-fg-3)' }}>greeting</span> is fully wired in conversational.json today
      </div>

      {/* ── preset_animations ────────────────────────────────────────── */}
      {/* ── Say Line ─────────────────────────────────────────────────
          The character's own turn, typed here instead of in a second tab on
          convorcher's chat UI (which needs the session id AND the character,
          story and features to match, or it opens a different session). */}
      <div style={{ marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>SAY LINE (forced)</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            value={sayText}
            onChange={e => setSayText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doSay() }}
            placeholder="[nodding] nice work! [clapping]"
            style={{
              flex: 1, minWidth: 0, background: 'var(--m-panel-hi)', color: 'var(--m-fg)',
              border: '1px solid var(--m-line-hi)', fontFamily: 'var(--m-font)', fontSize: 10,
              padding: '2px 6px', outline: 'none',
            }}
          />
          <button onClick={doSay} disabled={!sayText.trim()} style={sayText.trim() ? PLAY_BTN : DISABLED_BTN}>say</button>
        </div>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginTop: 2 }}>
          spoken verbatim — [brackets] play as gestures
        </div>
      </div>

      <div style={{ marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>PRESET_ANIMATIONS (direct)</div>
        {PRESET_ANIMATIONS.map(name => {
          const st = presets[name] ?? {}
          return (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <span style={{
                flex: 1, fontSize: 10,
                color: st.available ? 'var(--m-fg)' : 'var(--m-fg-3)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{name}</span>
              <button
                onClick={() => doPreset(name)}
                disabled={!st.available}
                title={st.available
                  ? (st.playing ? `stop ${name}` : `play ${name} directly (no graph)`)
                  : `${name} is not loaded for this character`}
                style={!st.available ? DISABLED_BTN : (st.playing ? STOP_BTN : PLAY_BTN)}
              >{st.playing ? '■ stop' : '▶ play'}</button>
            </div>
          )
        })}
        {PRESET_ANIMATIONS.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, fontStyle: 'italic' }}>
            — none curated —
          </div>
        )}
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginTop: 2 }}>
          plays on the mesh directly — bypasses the blueprint graph
        </div>
      </div>

      {/* ── Active ───────────────────────────────────────────────────── */}
      <div style={{ marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>
          ACTIVE ({active.length})
        </div>
        {active.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, fontStyle: 'italic' }}>— nothing playing —</div>
        )}
        {active.map(row => {
          const c = SOURCE_CHIP_COLORS[row.source]
          return (
            <div
              key={`${row.source}:${row.name}`}
              style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}
            >
              <span style={{
                background: c.bg, color: c.fg, border: `1px solid ${c.fg}`,
                borderRadius: 2, fontSize: 9, padding: '0 4px', minWidth: 44, textAlign: 'center',
              }}>{row.source}</span>
              <span style={{ flex: 1, fontFamily: 'var(--m-mono)', fontSize: 10, color: 'var(--m-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.name}
              </span>
              <MiniBar value={row.weight} />
              <span style={{ width: 32, textAlign: 'right', fontFamily: 'var(--m-mono)', fontSize: 9, color: 'var(--m-fg-3)' }}>
                {row.weight.toFixed(2)}
              </span>
            </div>
          )
        })}
      </div>

      {/* ── Debug Commands ───────────────────────────────────────────── */}
      <div style={{ marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>DEBUG (send to server)</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          <input
            value={debugCmd}
            onChange={e => setDebugCmd(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && debugCmd.trim()) { doDebugCmd(debugCmd.trim()); setDebugCmd('') } }}
            placeholder="trigger:greeting"
            style={{ ...SELECT_STYLE, flex: 1 }}
          />
          <button
            onClick={() => { if (debugCmd.trim()) { doDebugCmd(debugCmd.trim()); setDebugCmd('') } }}
            style={PLAY_BTN}
          >send</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {QUICK_TRIGGERS.map(n => (
            <button key={n} onClick={() => doDebugTrigger(n)} title={`trigger:${n}`} style={SMALL_BTN}>
              trigger:{n}
            </button>
          ))}
        </div>
      </div>

      {/* ── Hair shading ──────────────────────────────────────────────── */}
      {hairFlags && (
        <div style={{ marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }}>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 4 }}>HAIR SHADING</div>

          {/* debugMode — visualize intermediate shading terms. */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: 'var(--m-fg-3)', fontSize: 10, width: 52 }}>view</span>
            <select
              value={hairFlags.debugMode}
              onChange={e => setHairFlag('debugMode', e.target.value)}
              style={SELECT_STYLE}
              title="What to write to the splat colors — switch to diffuse/specular/etc. to inspect terms in isolation"
            >
              <option value="shaded">shaded (final)</option>
              <option value="diffuse">diffuse</option>
              <option value="specular">specular</option>
              <option value="albedo">albedo</option>
              <option value="bsdf">bsdf reflectance</option>
              <option value="roughness">roughness</option>
              <option value="normal">normal</option>
            </select>
          </div>

          {/* Boolean BSDF / SpecMod toggles. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!hairFlags.useNeuralBsdf}
                onChange={e => setHairFlag('useNeuralBsdf', e.target.checked)}
                style={{ accentColor: 'var(--m-online)' }}
              />
              <span style={{ color: 'var(--m-fg)' }}>NeuralBSDF</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!hairFlags.useSpecMod}
                onChange={e => setHairFlag('useSpecMod', e.target.checked)}
                style={{ accentColor: 'var(--m-online)' }}
              />
              <span style={{ color: 'var(--m-fg)' }}>spec_mod</span>
            </label>
          </div>
        </div>
      )}

      {lastFired && (
        <div style={{ marginTop: 8, color: 'var(--m-fg-3)', fontSize: 10, borderTop: '1px solid var(--m-line)', paddingTop: 6 }}>
          last: <span style={{ color: 'var(--m-fg-2)' }}>{lastFired}</span>
        </div>
      )}
    </div>
  )
}
